import { useCallback, useState } from 'react';
import { CloseRounded } from '@mui/icons-material';
import { Box, IconButton, Stepper, Step, StepLabel } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { TitleBox, HeaderEdit, Page } from './styles';
import { FormCriarConta } from '../FormCriarConta';
import { FormCriarEmpresa } from '../FormCriarEmpresa';
import { FormCriarUsuario } from '../FormCriarUsuario';
import { SuccessMessage } from '../SuccessMessage';
import { Company } from '../../../services';

import { LoadingBackdrop } from '../../../components/LoadingBackdrop';

const stepsLabel = ['Conta', 'Empresa', 'Usuário', 'Concluído'];

interface DrawerCriarContaProps {
  closeDrawer: any;
}

export const DrawerCriarConta = ({ closeDrawer }: DrawerCriarContaProps) => {
  const navigate = useNavigate();

  const [accountId, setAccountId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [company, setCompany] = useState({} as Company);
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set<number>());
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingText, setIsLoadingText] = useState('Carregando Informações');

  const isStepSkipped = useCallback(
    (step: number) => {
      return skipped.has(step);
    },
    [skipped],
  );

  const handleNext = useCallback(() => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep(prevActiveStep => prevActiveStep + 1);
    setSkipped(newSkipped);
  }, [activeStep, isStepSkipped, skipped]);

  const { reset } = useForm();

  const handleAccessAccount = useCallback(() => {
    navigate(`detalhes-conta/${accountId}`);
  }, [navigate, accountId]);

  const handleCancel = useCallback(() => {
    setActiveStep(0);
    closeDrawer();
    reset();
  }, [reset, closeDrawer]);

  return (
    <Box
      width="480px"
      margin="20px"
      display="flex"
      flexDirection="column"
      height="100%"
    >
      {isLoading && (
        <LoadingBackdrop
          component=""
          loading={isLoading}
          text={isLoadingText}
        />
      )}
      <HeaderEdit>
        <IconButton onClick={handleCancel}>
          <CloseRounded />
        </IconButton>
        <TitleBox className="edit-dados">Criar conta</TitleBox>
      </HeaderEdit>
      <Page display="flex" flexDirection="column" height="100%">
        <Stepper activeStep={activeStep} sx={{ mb: '30px' }}>
          {stepsLabel.map((label, index) => {
            const stepProps: { completed?: boolean } = {};
            const labelProps: {
              optional?: React.ReactNode;
            } = {};

            if (isStepSkipped(index)) {
              stepProps.completed = false;
            }
            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>

        {activeStep === 0 && (
          <FormCriarConta
            handleNext={handleNext}
            handleReset={reset}
            setAccountId={setAccountId}
            accountId=""
            setConta={() => null}
            setIsLoading={setIsLoading}
            setIsLoadingText={setIsLoadingText}
          />
        )}
        {activeStep === 1 && (
          <FormCriarEmpresa
            handleNext={handleNext}
            handleReset={reset}
            accountId={accountId}
            setCompanyId={setCompanyId}
            setCompany={setCompany}
            companyId=""
            setIsLoading={setIsLoading}
            setIsLoadingText={setIsLoadingText}
          />
        )}
        {activeStep === 2 && (
          <FormCriarUsuario
            handleNext={handleNext}
            handleReset={reset}
            companyId={companyId}
            company={company}
            accountId={accountId}
            setIsLoading={setIsLoading}
            setIsLoadingText={setIsLoadingText}
          />
        )}
        {activeStep === 3 && (
          <SuccessMessage
            handleAccessAccount={handleAccessAccount}
            handleGoBackAccounts={handleCancel}
          />
        )}
      </Page>
    </Box>
  );
};
